import { useEffect, useState } from "react";
import { ArrowDown, Loader2, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const Row = ({ label, thr, onChange, onRemove, canManage, testid }) => (
  <div className="flex items-center gap-2" data-testid={testid}>
    <span className="w-56 truncate text-sm text-slate-700 dark:text-slate-200">{label}</span>
    <Input type="number" step="0.5" min="0" max="40" value={thr.min_hours} disabled={!canManage}
      className="h-8 w-20" onChange={(e) => onChange({ ...thr, min_hours: e.target.value })}
      data-testid={`${testid}-hours`} />
    <span className="text-xs text-slate-500 dark:text-slate-400">hours and</span>
    <Input type="number" step="5" min="0" max="100" value={thr.min_pct} disabled={!canManage}
      className="h-8 w-20" onChange={(e) => onChange({ ...thr, min_pct: e.target.value })}
      data-testid={`${testid}-pct`} />
    <span className="text-xs text-slate-500 dark:text-slate-400">% of where they started</span>
    {onRemove && canManage && (
      <button type="button" onClick={onRemove} data-testid={`${testid}-remove`}
        className="text-slate-300 transition-colors hover:text-rose-600 dark:text-slate-500">
        <Trash2 className="h-4 w-4" />
      </button>
    )}
  </div>
);

/** Productivity → Settings → Dip alerts. Controls how big a two-bucket fall has
 *  to be before someone is flagged as sliding. */
export function ProdDipCard({ canManage }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addTeam, setAddTeam] = useState("");

  const load = () => api.get("/productivity/dip-thresholds").then(({ data }) => setCfg(data))
    .catch(() => toast.error("Couldn't load the dip thresholds"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/productivity/dip-thresholds", {
        min_hours: cfg.min_hours, min_pct: cfg.min_pct, team_overrides: cfg.team_overrides,
      });
      setCfg(data);
      toast.success("Dip thresholds saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that");
      load();
    } finally { setSaving(false); }
  };

  if (!cfg) return null;
  const overrides = cfg.team_overrides || {};

  return (
    <Card className="p-4" data-testid="prod-dip-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
        <ArrowDown className="h-4 w-4" /> Dip alerts
      </div>
      <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
        Someone is flagged as dipping when their productive hours fall in two buckets in a row
        (days on a short range, weeks on a long one). Both tests below have to pass, so small
        wobble stays quiet. Today&apos;s part-finished bucket is always ignored. Set 0 and 0 to
        flag every fall; a person in two teams gets the more sensitive of their teams&apos; settings.
      </p>

      <div className="space-y-2">
        <Row label="Everyone (default)" canManage={canManage}
          thr={{ min_hours: cfg.min_hours, min_pct: cfg.min_pct }}
          onChange={(t) => setCfg((c) => ({ ...c, min_hours: t.min_hours, min_pct: t.min_pct }))}
          testid="prod-dip-default" />
        {Object.entries(overrides).map(([team, thr]) => (
          <Row key={team} label={`Team: ${team}`} thr={thr} canManage={canManage}
            onChange={(t) => setCfg((c) => ({ ...c, team_overrides: { ...c.team_overrides, [team]: t } }))}
            onRemove={() => setCfg((c) => {
              const o = { ...c.team_overrides }; delete o[team]; return { ...c, team_overrides: o };
            })}
            testid={`prod-dip-team-${team.replace(/\s+/g, "-").toLowerCase()}`} />
        ))}
      </div>

      {canManage && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select value={addTeam} onChange={(e) => setAddTeam(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            data-testid="prod-dip-add-team">
            <option value="">Different threshold for a team…</option>
            {(cfg.teams || []).filter((t) => !overrides[t]).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <Button size="sm" variant="outline" disabled={!addTeam} data-testid="prod-dip-add-btn"
            onClick={() => {
              setCfg((c) => ({
                ...c,
                team_overrides: { ...c.team_overrides, [addTeam]: { min_hours: c.min_hours, min_pct: c.min_pct } },
              }));
              setAddTeam("");
            }}>
            <Plus className="mr-1 h-4 w-4" /> Add
          </Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="prod-dip-save">
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Save thresholds
          </Button>
        </div>
      )}
    </Card>
  );
}
